import ExcelJS from 'exceljs';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import { buildResponsesMatrix, type StageResponsesExport } from './responses-matrix';

export type { StageResponsesExport } from './responses-matrix';

export class StageResponsesFetchError extends Error {
  readonly name = 'StageResponsesFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/** Sinaliza "nenhuma resposta ainda" — o hook mostra um aviso em vez de baixar um arquivo vazio. */
export class StageResponsesEmptyError extends Error {
  readonly name = 'StageResponsesEmptyError';
}

/**
 * Sinaliza "esta etapa não tem formulário/avaliação vinculado" — o Core
 * responde 200 com `questions: []` (sem `form_id` nem `assessment_id`, o
 * survey não resolve). Distinto do `StageResponsesEmptyError`: ali existe
 * base de respostas e ninguém respondeu; aqui não existe base nenhuma.
 */
export class StageResponsesNoSurveyError extends Error {
  readonly name = 'StageResponsesNoSurveyError';
}

/**
 * Busca a base de respostas de uma etapa (module) via BFF recruiter. Rota crua
 * (`fetchClient` genérico) — o BFF proxia o Core e valida a resposta com o Zod
 * espelho; o tipo `StageResponsesExport` acompanha o schema do BFF.
 */
export async function fetchStageResponses(
  jobId: string,
  moduleId: string,
  signal?: AbortSignal
): Promise<StageResponsesExport> {
  const res = await fetchClient<StageResponsesExport>({
    method: 'GET',
    url: `/jobs/${jobId}/modules/${moduleId}/responses`,
    signal,
  });
  if (res.error) {
    throw new StageResponsesFetchError(
      `Failed to load stage responses (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data as StageResponsesExport;
}

/**
 * Gera o XLSX da base de respostas e dispara o download no browser. Reusa o
 * `exceljs` já presente no frontend recruiter (mesmo dep do parser de vagas).
 */
export async function downloadStageResponsesXlsx(
  data: StageResponsesExport,
  fileName: string
): Promise<void> {
  const { header, rows } = buildResponsesMatrix(data);

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Respostas');
  sheet.addRow(header);
  sheet.getRow(1).font = { bold: true };
  for (const row of rows) sheet.addRow(row);
  sheet.columns.forEach((col) => {
    col.width = 28;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, fileName);
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Slug ASCII seguro para o nome do arquivo a partir do nome da etapa. */
export function responsesFileName(stageName: string): string {
  const slug = stageName
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `respostas-${slug || 'etapa'}.xlsx`;
}
